import { useEffect, useState } from 'react'
import {
  triggerSentryError,
  ComcastErrorType,
} from '../../../../shared/helpers'
import Platform from '../../platforms'
import { UseResolveUrsInAppOptions, UseResolveUrsInAppResult } from './types'

/**
 * Resolves URS to actual video URL
 *
 * @param originalUrl - The URS or direct video URL to resolve
 * @param options - Configuration options
 * @param options.enabled - Whether to perform resolution (default: true)
 * @returns Object containing resolved URL, loading state, and error state
 *
 * @example
 * const { resolvedUrl, isResolving } = useResolveUrsInApp(
 *   'https://urs.pbs.org/redirect/abc123',
 *   { enabled: Platform.shouldResolveUrsUrlInApp() }
 * )
 */
export function useResolveUrsInApp(
  originalUrl: string,
  options: UseResolveUrsInAppOptions = {}
): UseResolveUrsInAppResult {
  const { enabled = true } = options
  const [resolvedUrl, setResolvedUrl] = useState<string>(originalUrl)
  const [isResolving, setIsResolving] = useState<boolean>(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (!enabled || !originalUrl) {
      setResolvedUrl(originalUrl)
      setIsResolving(false)
      return
    }

    let cancelled = false
    setIsResolving(true)

    const RESOLUTION_TIMEOUT_MS = 10000

    async function resolveUrl() {
      const controller = new AbortController()
      let timeoutId: NodeJS.Timeout | null = null

      try {
        timeoutId = setTimeout(() => {
          controller.abort()
        }, RESOLUTION_TIMEOUT_MS)

        const response = await fetch(originalUrl, {
          method: 'GET',
          signal: controller.signal,
        })

        if (timeoutId) {
          clearTimeout(timeoutId)
          timeoutId = null
        }

        if (!cancelled) {
          if (!response.ok) {
            throw new Error(
              `URS request failed with status ${response.status}: ${response.statusText}`
            )
          }

          const finalUrl = response.url

          if (finalUrl && finalUrl !== originalUrl) {
            setResolvedUrl(finalUrl)
            setError(null)
          } else {
            setResolvedUrl(originalUrl)
            setError(null)
          }
        }
      } catch (err) {
        if (timeoutId) clearTimeout(timeoutId)

        if (!cancelled) {
          const error = err as Error
          setError(error)
          const isTimeout = error.name === 'AbortError'
          const errorMessage = isTimeout
            ? `URS resolution timeout after ${RESOLUTION_TIMEOUT_MS}ms`
            : error.message

          console.error('[useResolveUrsInApp] URL resolution failed:', {
            originalUrl,
            error: errorMessage,
            isTimeout,
            fallback: 'Using original URL',
          })

          triggerSentryError(
            'URS Resolution Failed - Fallback to Original URL',
            'useResolveUrsInApp.ts',
            {
              originalUrl,
              errorMessage: errorMessage ?? 'Undefined error message',
              errorType: ComcastErrorType.network,
              platform: Platform.getPlatformName(),
              errorLocation: 'useResolveUrsInApp - resolveUrl',
              fallbackBehavior: 'Using original URS',
              isTimeout,
            }
          )

          setResolvedUrl(originalUrl)
        }
      } finally {
        if (!cancelled) {
          setIsResolving(false)
        }
      }
    }

    resolveUrl()

    return () => {
      cancelled = true
    }
  }, [originalUrl, enabled])

  return { resolvedUrl, isResolving, error }
}
